/**
 * How the six-digit lock passcode is masked.
 *
 * `type="password"` masks reliably, but it also declares the field a login
 * credential: browsers offer to save it, and `autocomplete="off"` does not stop
 * that. `-webkit-text-security` obfuscates an ordinary text field instead, so no
 * credential UI appears at all — but it is a non-standard property that Firefox
 * does not implement, where masking would vanish silently.
 *
 * Use the CSS property where it exists and fall back to `type="password"`
 * otherwise, so masking always holds and the credential prompts appear only on
 * the browsers that leave no alternative.
 */
const textSecuritySupported =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('-webkit-text-security', 'disc')
    : false;

/** The `type` attribute for a passcode input. */
export const pinInputType: 'text' | 'password' = textSecuritySupported ? 'text' : 'password';

/**
 * The `autocomplete` attribute for a passcode input. Kept out of the credential
 * manager's way in both branches — `off` is the strongest signal available, and
 * it is not relied upon on its own.
 */
export const pinAutoComplete = 'off';

/**
 * Builds the class list for a passcode input, adding the obfuscation utility
 * only where it is supported so the fallback does not carry a dead class.
 */
export function pinInputClassName(base: string): string {
  return textSecuritySupported ? `${base} [-webkit-text-security:disc]` : base;
}
